import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  IconButton,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import NumberLine from './NumberLine';
import NumberLineControls from './NumberLineControls';
import useNumberLine from './useNumberLine';
import {
  defaultNumberLineConfig,
  formatElementSummary,
  parseFraction,
  decimalToFraction,
  parseIntervalNotation,
  snapAndValidateBounds,
} from './numberLineUtils';

/**
 * Admin UI for configuring number line range and defining correct answer
 */
const NumberLineAdmin = ({
  config = defaultNumberLineConfig,
  onConfigChange,
  correctAnswer = { elements: [] },
  onAnswerChange,
}) => {
  const [localConfig, setLocalConfig] = useState({ ...defaultNumberLineConfig, ...config });

  // Notation text input state
  const [notationInput, setNotationInput] = useState('');
  const [notationError, setNotationError] = useState('');

  // State for fraction-supporting text inputs
  const [configInputs, setConfigInputs] = useState({
    min: decimalToFraction(config.min ?? defaultNumberLineConfig.min),
    max: decimalToFraction(config.max ?? defaultNumberLineConfig.max),
    step: decimalToFraction(config.step ?? defaultNumberLineConfig.step),
    labelInterval: decimalToFraction(config.labelInterval ?? defaultNumberLineConfig.labelInterval),
  });
  const [configInputErrors, setConfigInputErrors] = useState({
    min: '',
    max: '',
    step: '',
    labelInterval: '',
  });

  const {
    elements,
    mode,
    setMode,
    selectedId,
    setSelectedId,
    pendingValue,
    canUndo,
    addPoint,
    addInterval,
    addRay,
    removeElement,
    toggleEndpoint,
    undo,
    clear,
    handleLineClick,
    cancelOperation,
    setValue,
  } = useNumberLine(correctAnswer, localConfig);

  // Sync elements to parent
  useEffect(() => {
    if (onAnswerChange) {
      onAnswerChange({ elements });
    }
  }, [elements, onAnswerChange]);

  // Load correct answer when it changes externally
  useEffect(() => {
    if (correctAnswer?.elements) {
      setValue(correctAnswer);
    }
  }, []);

  // Sync configInputs when config changes from parent (preserve fraction notation)
  useEffect(() => {
    setConfigInputs((prev) => {
      const updates = {};
      for (const field of ['min', 'max', 'step', 'labelInterval']) {
        const configValue = localConfig[field];
        const currentParsed = parseFraction(prev[field]);
        if (!currentParsed.valid || Math.abs(currentParsed.value - configValue) > 1e-10) {
          updates[field] = decimalToFraction(configValue);
        }
      }
      return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
    });
  }, [localConfig]);

  // Handle config changes
  const handleConfigChange = (field, value) => {
    const newConfig = { ...localConfig, [field]: value };
    setLocalConfig(newConfig);
    if (onConfigChange) {
      onConfigChange(newConfig);
    }
  };

  // Handle fraction-supporting config input changes
  const handleFractionInputChange = (field, inputValue) => {
    setConfigInputs((prev) => ({ ...prev, [field]: inputValue }));

    const result = parseFraction(inputValue);
    if (!result.valid) {
      setConfigInputErrors((prev) => ({ ...prev, [field]: result.error }));
      return;
    }

    if ((field === 'step' || field === 'labelInterval') && result.value <= 0) {
      setConfigInputErrors((prev) => ({ ...prev, [field]: 'Must be positive' }));
      return;
    }

    setConfigInputErrors((prev) => ({ ...prev, [field]: '' }));
    handleConfigChange(field, result.value);
  };

  // Handle allowed modes change
  const handleAllowedModesChange = (newMode, checked) => {
    let newModes = [...(localConfig.allowedModes || ['point'])];
    if (checked && !newModes.includes(newMode)) {
      newModes.push(newMode);
    } else if (!checked) {
      newModes = newModes.filter((m) => m !== newMode);
    }
    // Ensure at least one mode is selected
    if (newModes.length === 0) {
      newModes = ['point'];
    }
    handleConfigChange('allowedModes', newModes);
  };

  // Handle notation text input submission
  const handleNotationSubmit = () => {
    const result = parseIntervalNotation(notationInput);
    if (!result.valid) {
      setNotationError(result.error);
      return;
    }

    const el = result.element;

    // Snap and validate bounds
    const boundsError = snapAndValidateBounds(el, localConfig);
    if (boundsError) {
      setNotationError(boundsError);
      return;
    }

    if (el.type === 'point') {
      addPoint(el.value, el.label);
    } else if (el.type === 'interval') {
      addInterval(el.start, el.end, el.startOpen, el.endOpen, el.startLabel, el.endLabel);
    } else if (el.type === 'ray') {
      addRay(el.origin, el.direction, el.originOpen, el.originLabel);
    }

    setNotationInput('');
    setNotationError('');
  };

  const handleElementClick = (id, endpoint) => {
    if (endpoint) {
      // Clicked on endpoint - toggle open/closed
      toggleEndpoint(id, endpoint);
    } else if (selectedId === id) {
      // Double-click to delete
      removeElement(id);
    } else {
      setSelectedId(id);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Configuration Section */}
      <Typography variant="h6" fontWeight="bold">
        Number Line Configuration
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Min Value"
          size="small"
          value={configInputs.min}
          onChange={(e) => handleFractionInputChange('min', e.target.value)}
          error={!!configInputErrors.min}
          helperText={configInputErrors.min || 'e.g., -10, -1/2'}
          sx={{ width: 120 }}
        />
        <TextField
          label="Max Value"
          size="small"
          value={configInputs.max}
          onChange={(e) => handleFractionInputChange('max', e.target.value)}
          error={!!configInputErrors.max}
          helperText={configInputErrors.max || 'e.g., 10, 3/2'}
          sx={{ width: 120 }}
        />
        <TextField
          label="Step"
          size="small"
          value={configInputs.step}
          onChange={(e) => handleFractionInputChange('step', e.target.value)}
          error={!!configInputErrors.step}
          helperText={configInputErrors.step || 'e.g., 1, 1/2'}
          sx={{ width: 120 }}
        />
        <TextField
          label="Label Interval"
          size="small"
          value={configInputs.labelInterval}
          onChange={(e) => handleFractionInputChange('labelInterval', e.target.value)}
          error={!!configInputErrors.labelInterval}
          helperText={configInputErrors.labelInterval || 'e.g., 1, 1/2'}
          sx={{ width: 140 }}
        />
        <TextField
          label="Tolerance"
          type="number"
          size="small"
          value={localConfig.tolerance}
          onChange={(e) => handleConfigChange('tolerance', Number(e.target.value))}
          inputProps={{ min: 0.001, step: 0.01 }}
          sx={{ width: 100 }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={localConfig.showTicks !== false}
              onChange={(e) => handleConfigChange('showTicks', e.target.checked)}
            />
          }
          label="Show Ticks"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={localConfig.showLabels !== false}
              onChange={(e) => handleConfigChange('showLabels', e.target.checked)}
            />
          }
          label="Show Labels"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={localConfig.snapToTicks !== false}
              onChange={(e) => handleConfigChange('snapToTicks', e.target.checked)}
            />
          }
          label="Snap to Ticks"
        />
      </Box>

      {/* Allowed Modes for Students */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Allowed Modes for Students:
        </Typography>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={(localConfig.allowedModes || ['point']).includes('point')}
                onChange={(e) => handleAllowedModesChange('point', e.target.checked)}
              />
            }
            label="Point"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={(localConfig.allowedModes || []).includes('interval')}
                onChange={(e) => handleAllowedModesChange('interval', e.target.checked)}
              />
            }
            label="Interval"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={(localConfig.allowedModes || []).includes('ray')}
                onChange={(e) => handleAllowedModesChange('ray', e.target.checked)}
              />
            }
            label="Ray"
          />
        </FormGroup>
      </Box>

      <Divider />

      {/* Answer Definition Section */}
      <Typography variant="h6" fontWeight="bold">
        Define Correct Answer
      </Typography>

      {/* Instructions Box */}
      <Box sx={{ p: 1.5, backgroundColor: 'rgba(33, 150, 243, 0.08)', borderRadius: 1, border: '1px solid rgba(33, 150, 243, 0.2)' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          How to use:
        </Typography>
        <Typography variant="body2" component="div" sx={{ '& ul': { m: 0, pl: 2.5 }, '& li': { mb: 0.5 } }}>
          <ul>
            <li>Click on the number line to place a point, or start an interval/ray, or type notation below (e.g., <code>[1, 5)</code>, <code>(-inf, 0]</code>, <code>1.1</code>)</li>
            <li>Click an element to select it (black border), click again to delete</li>
            <li>For intervals/rays, click on an endpoint to toggle:
              <ul>
                <li><strong>● Filled circle</strong> = Inclusive (includes the value, e.g., x ≤ 3)</li>
                <li><strong>○ Open circle</strong> = Exclusive (excludes the value, e.g., x &lt; 3)</li>
              </ul>
            </li>
          </ul>
        </Typography>
      </Box>

      {/* Mode Controls */}
      <NumberLineControls
        mode={mode}
        onModeChange={setMode}
        allowedModes={['point', 'interval', 'ray']}
        canUndo={canUndo}
        onUndo={undo}
        onClear={clear}
        pendingValue={pendingValue}
        onCancelOperation={cancelOperation}
      />

      {/* Number Line */}
      <NumberLine
        config={localConfig}
        elements={elements}
        pendingValue={pendingValue}
        mode={mode}
        readOnly={false}
        onLineClick={handleLineClick}
        onElementClick={handleElementClick}
        selectedId={selectedId}
        width={600}
        height={100}
      />

      {/* Notation Text Input */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          placeholder="e.g., [1, 5), (-inf, 0], 1.1"
          value={notationInput}
          onChange={(e) => {
            setNotationInput(e.target.value);
            if (notationError) setNotationError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleNotationSubmit();
            }
          }}
          error={!!notationError}
          helperText={notationError}
          sx={{ flex: 1, maxWidth: 300 }}
        />
        <IconButton
          onClick={handleNotationSubmit}
          color="primary"
          disabled={!notationInput.trim()}
          sx={{ mt: 0.25 }}
        >
          <AddCircleOutlineIcon />
        </IconButton>
      </Box>

      {/* Current Elements Display */}
      {elements.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Current Answer Elements:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {elements.map((el, i) => (
              <Chip
                key={el.id || i}
                label={formatElementSummary(el)}
                size="small"
                onDelete={() => removeElement(el.id)}
                color={selectedId === el.id ? 'primary' : 'default'}
                onClick={() => setSelectedId(el.id)}
              />
            ))}
          </Box>
        </Box>
      )}

      {elements.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No elements added yet. Click on the number line to add points, intervals, or rays.
        </Typography>
      )}
    </Box>
  );
};

export default NumberLineAdmin;
